/**
 * Viewport utilities for configurable breakpoints
 */

/**
 * Migrate old data format to new viewports array
 * Call this when loading project data
 *
 * Handles two legacy formats:
 * 1. Main app format: { urls: { figmaDesktopUrl, figmaMobileUrl } }
 * 2. Plugin format: { desktopUrl, mobileUrl }
 */
export function migrateToViewports(projectData) {
  // Already migrated - check for non-empty viewports array
  if (projectData.viewports && Array.isArray(projectData.viewports) && projectData.viewports.length > 0) {
    return projectData;
  }

  // Handle main app format: urls.figmaDesktopUrl
  const desktopUrl = projectData.urls?.figmaDesktopUrl || null;
  const mobileUrl = projectData.urls?.figmaMobileUrl || null;

  // Handle plugin format: desktopUrl (no urls wrapper)
  const pluginDesktopUrl = projectData.desktopUrl || null;
  const pluginMobileUrl = projectData.mobileUrl || null;

  // Use whichever format exists
  const finalDesktop = desktopUrl || pluginDesktopUrl;
  const finalMobile = mobileUrl || pluginMobileUrl;

  return {
    ...projectData,
    viewports: [
      { name: "Mobile", minWidth: 0, figmaUrl: finalMobile || null },
      { name: "Desktop", minWidth: 768, figmaUrl: finalDesktop || null }
    ]
  };
}

/**
 * Get the active viewport based on window width
 */
export function getActiveViewport(viewports, windowWidth) {
  if (!viewports || viewports.length === 0) return null;
  const sorted = [...viewports].sort((a, b) => b.minWidth - a.minWidth);
  return sorted.find(v => windowWidth >= v.minWidth) || sorted[sorted.length - 1];
}

/**
 * Get the effective Figma URL for a viewport (with inheritance)
 * Returns empty string if no URL is found (never returns null)
 */
export function getEffectiveFigmaUrl(viewport, allViewports) {
  if (!viewport || !allViewports || allViewports.length === 0) return '';
  if (viewport.figmaUrl) return viewport.figmaUrl;

  // Sort ascending and find current index
  const sorted = [...allViewports].sort((a, b) => a.minWidth - b.minWidth);
  const currentIndex = sorted.findIndex(v => v.minWidth === viewport.minWidth);

  // Look for next larger viewport with URL
  for (let i = currentIndex + 1; i < sorted.length; i++) {
    if (sorted[i].figmaUrl) return sorted[i].figmaUrl;
  }

  // Fallback: look smaller
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (sorted[i].figmaUrl) return sorted[i].figmaUrl;
  }

  return '';
}

/**
 * Get smart icon based on pixel width
 */
export function getViewportIcon(minWidth) {
  if (minWidth < 768) return '📱';   // Mobile
  if (minWidth < 1440) return '💻';  // Tablet/Desktop (laptop)
  return '🖥️';                       // Large Desktop (monitor)
}

/**
 * Validate viewports array
 */
export function validateViewports(viewports) {
  const errors = [];

  if (!viewports || viewports.length < 2) {
    errors.push('Minimum 2 viewports required');
  }
  if (viewports && viewports.length > 6) {
    errors.push('Maximum 6 viewports allowed');
  }

  if (viewports && viewports.length > 0) {
    // Check for base viewport (0px)
    if (!viewports.some(v => v.minWidth === 0)) {
      errors.push('Must have a base viewport at 0px');
    }

    // Check for duplicate widths
    const widths = viewports.map(v => v.minWidth);
    const duplicates = widths.filter((w, i) => widths.indexOf(w) !== i);
    if (duplicates.length > 0) {
      errors.push('Breakpoint values must be unique');
    }

    // Check for duplicate names
    const names = viewports.map(v => v.name.toLowerCase());
    const dupNames = names.filter((n, i) => names.indexOf(n) !== i);
    if (dupNames.length > 0) {
      errors.push('Viewport names must be unique');
    }

    // Check at least one URL exists
    if (!viewports.some(v => v.figmaUrl)) {
      errors.push('At least one viewport must have a Figma URL');
    }

    // Check minWidth range
    if (viewports.some(v => v.minWidth < 0 || v.minWidth > 3840)) {
      errors.push('Breakpoint values must be between 0 and 3840');
    }
  }

  return errors;
}

/**
 * Get suggested name and width for new viewport
 */
export function suggestNewViewport(existingViewports) {
  if (!existingViewports || existingViewports.length === 0) {
    return { minWidth: 768, name: 'Desktop' };
  }

  const sorted = [...existingViewports].sort((a, b) => a.minWidth - b.minWidth);
  const existingWidths = sorted.map(v => v.minWidth);
  const largestWidth = existingWidths[existingWidths.length - 1];

  // Suggest next logical breakpoint
  const suggestions = [
    { width: 768, name: 'Tablet' },
    { width: 1024, name: 'Desktop' },
    { width: 1440, name: 'Large Desktop' },
    { width: 1920, name: '4K' },
  ];

  for (const s of suggestions) {
    if (!existingWidths.includes(s.width) && s.width > largestWidth) {
      return { minWidth: s.width, name: s.name };
    }
  }

  // Fallback
  return { minWidth: largestWidth + 200, name: `Viewport ${existingViewports.length + 1}` };
}

/**
 * Find the viewport that a given viewport inherits from
 */
export function getInheritingFrom(viewport, allViewports) {
  if (!viewport || !allViewports || viewport.figmaUrl) return null;

  // Sort ascending and find current index
  const sorted = [...allViewports].sort((a, b) => a.minWidth - b.minWidth);
  const currentIndex = sorted.findIndex(v => v.minWidth === viewport.minWidth);

  // Look for next larger viewport with URL
  for (let i = currentIndex + 1; i < sorted.length; i++) {
    if (sorted[i].figmaUrl) return sorted[i].name;
  }

  // Fallback: look smaller
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (sorted[i].figmaUrl) return sorted[i].name;
  }

  return null;
}

/**
 * True when a URL is a Figma file/design link rather than an embeddable
 * prototype link. Shared by the viewport editor (inline warning) and the
 * Aero save guard so both reject the same inputs. Empty is not "wrong".
 */
export function isWrongUrlType(url) {
  if (!url) return false;
  return url.includes('figma.com/file') || url.includes('figma.com/design');
}

/**
 * Get default viewports for a new project
 */
export function getDefaultViewports() {
  return [
    { name: "Mobile", minWidth: 0, figmaUrl: null },
    { name: "Desktop", minWidth: 768, figmaUrl: null }
  ];
}

// ---- Breakpoints, as the editor shows them (Figmafolio and Aero) --------------------------------
// The biggest breakpoint is the one the others borrow from: an empty row shows the next larger
// row's link, or a smaller one when there is none larger (getEffectiveFigmaUrl). The data does not
// change shape: always a 0px base row plus the others, each with a minimum width.

/** "Mobile" is how older sites named the 0px row; the editor calls it Phone. Names stay as saved. */
export function displayViewportName(name) {
  return name === 'Mobile' ? 'Phone' : name;
}

/**
 * True for the starting state: the two default rows with no phone link. The editor then shows one
 * row, "All screens", bound to the Desktop row (an empty Phone row borrows it).
 */
export function isSingleLinkMode(viewports) {
  if (!viewports || viewports.length !== 2) return false;
  const base = viewports.find((v) => v.minWidth === 0);
  const desktop = viewports.find((v) => v.minWidth === 768);
  if (!base || !desktop) return false;
  return !base.figmaUrl && /^(mobile|phone)$/i.test(base.name) && /^desktop$/i.test(desktop.name);
}

/** "up to 767px", "768 to 1023px", "1024px and up": the range a breakpoint covers, from its neighbours. */
export function rangeLabel(viewport, viewports) {
  const sorted = [...viewports].sort((a, b) => a.minWidth - b.minWidth);
  const i = sorted.findIndex((v) => v.minWidth === viewport.minWidth);
  const next = sorted[i + 1];
  if (!next) return viewport.minWidth === 0 ? 'Every screen size' : `${viewport.minWidth}px and up`;
  if (viewport.minWidth === 0) return `up to ${next.minWidth - 1}px`;
  return `${viewport.minWidth} to ${next.minWidth - 1}px`;
}

const TABLET_WIDTH = 768;
const LARGE_WIDTH = 1440;
const SHIFT_SLOTS = [1024, 1280, 1440, 1536, 1920, 2560, 3200, 3840];

/** What the Add breakpoint menu offers now: [{ id, name, hint }]. Empty at the 6 breakpoint limit. */
export function availableBreakpoints(viewports) {
  if (!viewports || viewports.length >= 6) return [];
  const has = (re) => viewports.some((v) => re.test(v.name));
  const items = [];
  if (!has(/^tablet$/i)) items.push({ id: 'tablet', name: 'Tablet', hint: '768 to 1023px' });
  if (!has(/^large desktop$/i) && !viewports.some((v) => v.minWidth >= LARGE_WIDTH)) {
    items.push({ id: 'large', name: 'Large desktop', hint: '1440px and up' });
  }
  items.push({ id: 'custom', name: 'Custom width…', hint: '' });
  return items;
}

/**
 * Add a breakpoint. Returns { viewports } or { error }. A tablet lands at 768px; a row that is
 * already there moves up to the next free slot (1024px), so the bigger one stays bigger. The new
 * row starts empty, so it borrows the link above it and visitors see no change until a link is pasted.
 */
export function addBreakpoint(viewports, kind, customWidth) {
  if (viewports.length >= 6) return { error: 'You can have up to 6 breakpoints' };
  const used = new Set(viewports.map((v) => v.minWidth));
  if (kind === 'large') {
    if (used.has(LARGE_WIDTH)) return { error: `A breakpoint already starts at ${LARGE_WIDTH}px` };
    return { viewports: [...viewports, { name: 'Large desktop', minWidth: LARGE_WIDTH, figmaUrl: null }] };
  }
  if (kind === 'custom') {
    const w = parseInt(customWidth, 10);
    if (isNaN(w) || w < 1 || w > 3840) return { error: 'Enter a width between 1 and 3840' };
    if (used.has(w)) return { error: `A breakpoint already starts at ${w}px` };
    return { viewports: [...viewports, { name: `${w}px`, minWidth: w, figmaUrl: null }] };
  }
  // tablet
  let next = viewports;
  if (used.has(TABLET_WIDTH)) {
    const slot = SHIFT_SLOTS.find((w) => !used.has(w));
    if (!slot) return { error: 'No room to move the bigger breakpoint up' };
    next = viewports.map((v) => (v.minWidth === TABLET_WIDTH ? { ...v, minWidth: slot } : v));
  }
  return { viewports: [...next, { name: 'Tablet', minWidth: TABLET_WIDTH, figmaUrl: null }] };
}

/**
 * Convert viewports array back to legacy format for backward compatibility
 * This ensures old consumers can still read the data
 */
export function viewportsToLegacyFormat(viewports) {
  if (!viewports || viewports.length === 0) {
    return { figmaDesktopUrl: '', figmaMobileUrl: '' };
  }

  const sorted = [...viewports].sort((a, b) => a.minWidth - b.minWidth);

  // Mobile is the smallest (0px)
  const mobileViewport = sorted.find(v => v.minWidth === 0);
  // Desktop is the largest non-mobile
  const desktopViewport = sorted.filter(v => v.minWidth > 0).pop();

  const mobileUrl = mobileViewport ? getEffectiveFigmaUrl(mobileViewport, viewports) : null;
  const desktopUrl = desktopViewport ? getEffectiveFigmaUrl(desktopViewport, viewports) : null;

  return {
    figmaDesktopUrl: desktopUrl || '',
    figmaMobileUrl: mobileUrl || ''
  };
}
